import { c as _c } from "react/compiler-runtime";
import clsx from "clsx";
import { cloneElement, ComponentProps, ElementType, FC, PropsWithChildren, ReactElement } from "react";
import Icon, { IconType } from "../Icon";
import Link from "../internal/Link";
import Loader from "../Loader";
import { ButtonProps, ButtonSize, ButtonVariant } from "./types";
const disabledCommon = "disabled:opacity-50 disabled:cursor-not-allowed";
const focusCommon = "focusable";
const ghostCommon = "w-fit";
const variants: Record<ButtonVariant, string> = {
  primary: `text-neutral-0 bg-primary-800 border border-primary-800 hover:bg-primary-825 hover:border-primary-825 active:bg-primary-850 active:border-primary-850 ${disabledCommon} ${focusCommon} focus-visible:outline-primary-800`,
  primary_inverted: `text-neutral-675 bg-neutral-0 hover:bg-neutral-25 active:bg-neutral-50 ${disabledCommon} ${focusCommon} focus-visible:outline-neutral-0`,
  ghost: `text-primary-800 bg-transparent hover:text-primary-825 active:text-primary-850 ${disabledCommon} ${focusCommon} focus-visible:outline-primary-800 ${ghostCommon}`,
  ghost_neutral: `text-neutral-675 bg-transparent hover:text-neutral-575 active:text-neutral-425 ${disabledCommon} ${focusCommon} focus-visible:outline-primary-800 ${ghostCommon}`,
  ghost_danger: `text-error-700 bg-transparent hover:border-error-750 active:text-error-775 ${disabledCommon} ${focusCommon} focus-visible:outline-primary-800 ${ghostCommon}`,
  ghost_inverted: `text-neutral-0 bg-transparent hover:text-neutral-25 active:text-neutral-50 ${disabledCommon} ${focusCommon} focus-visible:outline-neutral-0 ${ghostCommon}`,
  danger: `text-neutral-0 bg-error-700 hover:bg-error-750 active:bg-error-775 ${disabledCommon} ${focusCommon} focus-visible:outline-error-700`,
  outline_danger: `text-error-700 bg-neutral-0 border border-neutral-50 hover:border-error-750 hover:text-error-750 active:border-error-775 active:text-error-775 ${disabledCommon} ${focusCommon}`,
  outline_primary: `text-primary-800 bg-neutral-0 border border-neutral-50 hover:border-primary-825 hover:text-primary-825 active:border-primary-850 active:text-primary-850 ${disabledCommon} ${focusCommon} focus-visible:outline-primary-800`,
  outline_inverted: `text-neutral-0 border border-neutral-0/50 hover:border-neutral-0 active:border-neutral-0/60 ${disabledCommon} ${focusCommon} focus-visible:outline-neutral-0`,
  icon: `text-primary-800 bg-neutral-0 !rounded-full shadow-1 !p-2s hover:text-neutral-0 hover:bg-primary-825 active:text-neutral-0 active:bg-primary-850 ${disabledCommon} ${focusCommon} focus-visible:outline-primary-800 focus-visible:outline-offset-0`,
  rose_red: `text-neutral-0 bg-rose-425 hover:bg-rose-675 active:bg-rose-750 ${disabledCommon} ${focusCommon} focus-visible:outline-rose-425`,
  han_purple: `text-neutral-0 bg-purple-725 hover:bg-purple-775 active:bg-purple-850 ${disabledCommon} ${focusCommon} focus-visible:outline-purple-725`,
  ghost_han_purple: `text-purple-725 bg-transparent hover:text-purple-775 active:text-purple-850 ${disabledCommon} ${focusCommon} focus-visible:outline-purple-725  ${ghostCommon}`,
  marigold: `text-neutral-675 bg-marigold-100 hover:bg-marigold-325 active:bg-marigold-525 ${disabledCommon} ${focusCommon} focus-visible:outline-marigold-100`,
  mindaro: `text-neutral-675 bg-mindaro-325 hover:bg-mindaro-575 active:bg-mindaro-675 ${disabledCommon} ${focusCommon} focus-visible:outline-mindaro-325`,
  ghost_mindaro: `text-mindaro-325 bg-transparent hover:text-mindaro-575 active:text-mindaro-675 ${disabledCommon} ${focusCommon} focus-visible:outline-mindaro-325 ${ghostCommon}`,
  neutral: `text-neutral-425 bg-neutral-12 border border-neutral-12 hover:bg-neutral-25 hover:border-neutral-25 active:bg-neutral-50 active:border-neutral-50 ${disabledCommon} ${focusCommon}`
};
const invertedLoader: Record<ButtonVariant, boolean> = {
  primary: true,
  ghost_neutral: false,
  primary_inverted: false,
  ghost: false,
  ghost_inverted: true,
  ghost_danger: false,
  danger: true,
  outline_danger: false,
  outline_primary: false,
  outline_inverted: true,
  icon: false,
  rose_red: true,
  han_purple: true,
  ghost_han_purple: false,
  marigold: false,
  mindaro: false,
  ghost_mindaro: false,
  neutral: false
};

// Buttons are emphasis type: `semibold` (700), the same weight as labels, eyebrows and
// numbers. The sizes used to sit at 400 (`lg`) and 500 (`book`), which read as body text on
// a filled surface.
const sizes: Record<ButtonSize, string> = {
  lg: "px-2s s:px-2m py-[15px] text-button-1 font-semibold rounded-s",
  md: "px-2s s:px-3s py-[10px] s:py-[13px] text-button-2 font-medium rounded-s",
  sm: "px-2s py-[7px] text-app-button-1 font-book rounded-s",
  xs: "px-2xs py-[3px] text-app-body-2 font-book rounded-xs"
};
const iconEndSizes: Record<ButtonSize, string> = {
  lg: "ml-3xs",
  md: "ml-2xs !text-button-1",
  sm: "ml-2xs !text-button-1",
  xs: "ml-xs !text-app-body-2"
};
const iconStartSizes: Record<ButtonSize, string> = {
  lg: "mr-3xs",
  md: "mr-2xs !text-button-1",
  sm: "mr-2xs !text-button-1",
  xs: "mr-xs !text-app-body-2"
};
const ButtonIcon: FC<{
  type?: IconType | ReactElement | null;
  className?: string;
  size: ButtonSize;
  color?: string;
}> = t0 => {
  const $ = _c(13);
  const {
    type,
    className,
    size,
    color
  } = t0;
  let iconSize = 20;
  let iconClass = "";
  if (size === "lg") {
    iconSize = 24;
  } else {
    if (size === "xs") {
      iconSize = 16;
      iconClass = "flex justify-center items-center w-2s h-2s";
    }
  }
  if (typeof type === "string") {
    const t1 = iconSize;
    let t2;
    if ($[0] !== className || $[1] !== iconClass) {
      t2 = clsx(className, iconClass);
      $[0] = className;
      $[1] = iconClass;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = color || "currentColor";
    let t4;
    if ($[3] !== iconSize || $[4] !== t2 || $[5] !== t3 || $[6] !== type) {
      t4 = <Icon size={t1} className={t2} type={type} fill={t3} />;
      $[3] = iconSize;
      $[4] = t2;
      $[5] = t3;
      $[6] = type;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (type) {
    const t1 = type as unknown as ReactElement<ComponentProps<typeof Icon>>;
    let t2;
    if ($[8] !== className || $[9] !== color || $[10] !== iconSize || $[11] !== t1) {
      t2 = cloneElement(t1, {
        className,
        size: iconSize,
        fill: color
      });
      $[8] = className;
      $[9] = color;
      $[10] = iconSize;
      $[11] = t1;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  return null;
};
const Button = t0 => {
  const $ = _c(83);
  let as;
  let children;
  let className;
  let disabled;
  let iconColor;
  let iconEnd;
  let iconStart;
  let isLoading;
  let onClick;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== t0) {
    ({
      variant: t1,
      size: t2,
      iconStart,
      iconEnd,
      type: t3,
      disabled,
      children,
      className,
      as,
      isLoading,
      onClick,
      iconColor,
      fullWidth: t4,
      paddingX: t5,
      shadow: t6,
      center: t7,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = as;
    $[2] = children;
    $[3] = className;
    $[4] = disabled;
    $[5] = iconColor;
    $[6] = iconEnd;
    $[7] = iconStart;
    $[8] = isLoading;
    $[9] = onClick;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    as = $[1];
    children = $[2];
    className = $[3];
    disabled = $[4];
    iconColor = $[5];
    iconEnd = $[6];
    iconStart = $[7];
    isLoading = $[8];
    onClick = $[9];
    props = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
  }
  const variant = t1 === undefined ? "primary" : t1;
  const size = t2 === undefined ? "lg" : t2;
  const type = t3 === undefined ? "button" : t3;
  const fullWidth = t4 === undefined ? false : t4;
  const paddingX = t5 === undefined ? true : t5;
  const shadow = t6 === undefined ? false : t6;
  const center = t7 === undefined ? true : t7;
  const variationClass = variants[variant];
  const sizeClass = sizes[size];
  const iconOnly = !children && (iconStart || iconEnd);
  let t8;
  if ($[18] !== variant) {
    t8 = variant.startsWith("ghost");
    $[18] = variant;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const isGhost = t8;
  const t9 = iconOnly && size === "xs";
  const t10 = iconOnly && size === "sm";
  const t11 = iconOnly && size === "md";
  const t12 = iconOnly && size === "lg";
  const t13 = !fullWidth;
  const t14 = !iconOnly && !isGhost;
  const t15 = !paddingX;
  const t16 = variant === "ghost_inverted" && size === "sm";
  let t17;
  if ($[20] !== center || $[21] !== className || $[22] !== shadow || $[23] !== sizeClass || $[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t14 || $[29] !== t15 || $[30] !== t16 || $[31] !== t9 || $[32] !== variationClass) {
    t17 = clsx("transition flex items-center no-underline", variationClass, sizeClass, className, {
      "justify-center": center,
      "!px-[3px]": t9,
      "!px-[7px]": t10,
      "!p-3xs": t11,
      "!p-s": t12,
      "m:w-fit": t13,
      "w-full": t14,
      "!px-none": t15,
      "shadow-2": shadow,
      "hover:border-neutral-0 hover:border-opacity-40": t16
    });
    $[20] = center;
    $[21] = className;
    $[22] = shadow;
    $[23] = sizeClass;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
    $[31] = t9;
    $[32] = variationClass;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const combineClassName = t17;
  let content;
  if (variant === "icon") {
    const icon = iconStart || iconEnd;
    let t18;
    if ($[34] !== icon || $[35] !== iconColor || $[36] !== size) {
      t18 = <ButtonIcon size={size} type={icon} color={iconColor} />;
      $[34] = icon;
      $[35] = iconColor;
      $[36] = size;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    content = t18;
  } else {
    let t18;
    if ($[38] !== iconColor || $[39] !== iconOnly || $[40] !== iconStart || $[41] !== size) {
      t18 = iconStart && <ButtonIcon color={iconColor} className={clsx(iconStartSizes[size], {
        "!m-none": iconOnly
      })} size={size} type={iconStart} />;
      $[38] = iconColor;
      $[39] = iconOnly;
      $[40] = iconStart;
      $[41] = size;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[43] !== iconColor || $[44] !== iconEnd || $[45] !== iconOnly || $[46] !== size) {
      t19 = iconEnd && <ButtonIcon color={iconColor} className={clsx(iconEndSizes[size], {
        "!m-none": iconOnly
      })} size={size} type={iconEnd} />;
      $[43] = iconColor;
      $[44] = iconEnd;
      $[45] = iconOnly;
      $[46] = size;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    let t20;
    if ($[48] !== children || $[49] !== t18 || $[50] !== t19) {
      t20 = <>{t18}{children}{t19}</>;
      $[48] = children;
      $[49] = t18;
      $[50] = t19;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    content = t20;
  }
  if (isLoading) {
    if (iconOnly) {
      const t18 = size === "xs";
      let t19;
      if ($[52] !== t18) {
        t19 = clsx("flex items-center justify-center", {
          "w-2s h-2s": t18
        });
        $[52] = t18;
        $[53] = t19;
      } else {
        t19 = $[53];
      }
      const t20 = size === "xs" ? "sm" : "md";
      const t21 = invertedLoader[variant];
      let t22;
      if ($[54] !== t20 || $[55] !== t21) {
        t22 = <Loader size={t20} className="flex" inverted={t21} />;
        $[54] = t20;
        $[55] = t21;
        $[56] = t22;
      } else {
        t22 = $[56];
      }
      let t23;
      if ($[57] !== t19 || $[58] !== t22) {
        t23 = <span className={t19}>{t22}</span>;
        $[57] = t19;
        $[58] = t22;
        $[59] = t23;
      } else {
        t23 = $[59];
      }
      content = t23;
    } else {
      if (iconStart) {
        const t18 = invertedLoader[variant];
        const t19 = iconStartSizes[size];
        let t20;
        if ($[60] !== t19) {
          t20 = clsx("flex", t19);
          $[60] = t19;
          $[61] = t20;
        } else {
          t20 = $[61];
        }
        let t21;
        if ($[62] !== t18 || $[63] !== t20) {
          t21 = <Loader size="md" inverted={t18} className={t20} />;
          $[62] = t18;
          $[63] = t20;
          $[64] = t21;
        } else {
          t21 = $[64];
        }
        let t22;
        if ($[65] !== children || $[66] !== t21) {
          t22 = <div className="flex relative justify-center items-center">{t21}{children}</div>;
          $[65] = children;
          $[66] = t21;
          $[67] = t22;
        } else {
          t22 = $[67];
        }
        content = t22;
      } else {
        const t18 = invertedLoader[variant];
        let t19;
        if ($[68] !== t18) {
          t19 = <span className="absolute inset-none flex items-center justify-center"><Loader size="md" className="flex" inverted={t18} /></span>;
          $[68] = t18;
          $[69] = t19;
        } else {
          t19 = $[69];
        }
        let t20;
        if ($[70] !== content) {
          t20 = <div className="invisible flex items-center">{content}</div>;
          $[70] = content;
          $[71] = t20;
        } else {
          t20 = $[71];
        }
        let t21;
        if ($[72] !== t19 || $[73] !== t20) {
          t21 = <div className="flex relative justify-center items-center">{t19}{t20}</div>;
          $[72] = t19;
          $[73] = t20;
          $[74] = t21;
        } else {
          t21 = $[74];
        }
        content = t21;
      }
    }
  }
  const Component = as ? as : props.href ? Link : "button";
  const t18 = !!disabled;
  const t19 = type || undefined;
  const t20 = isLoading ? undefined : onClick;
  let t21;
  if ($[75] !== Component || $[76] !== combineClassName || $[77] !== content || $[78] !== props || $[79] !== t18 || $[80] !== t19 || $[81] !== t20) {
    t21 = <Component disabled={t18} type={t19} className={combineClassName} onClick={t20} {...props}>{content}</Component>;
    $[75] = Component;
    $[76] = combineClassName;
    $[77] = content;
    $[78] = props;
    $[79] = t18;
    $[80] = t19;
    $[81] = t20;
    $[82] = t21;
  } else {
    t21 = $[82];
  }
  return t21;
};
export default Button;